import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Key, Plus, Trash2, Copy, Check } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input } from '../components/ui/Input.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface HeadlessToken {
  id: string;
  name: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

function fmt(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Raw Token Reveal Modal ────────────────────────────────────────────────────

function TokenRevealModal({ token, onClose }: { token: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(token).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <Modal open title="API Token Created" onClose={onClose} size="md">
      <div className="space-y-4">
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
          <p className="text-sm font-medium text-amber-800">
            Copy this token now — it will never be shown again.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs font-mono bg-zinc-100 px-3 py-2 rounded-lg break-all text-zinc-800 border border-zinc-200">
            {token}
          </code>
          <button
            onClick={handleCopy}
            title="Copy to clipboard"
            className="flex-shrink-0 p-2 rounded-lg hover:bg-zinc-100 text-zinc-500 hover:text-zinc-800 transition-colors"
          >
            {copied ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
          </button>
        </div>
      </div>
      <ModalFooter>
        <Button onClick={onClose}>Done</Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CmsHeadlessTokensPage() {
  const qc = useQueryClient();
  const toast = useToast();

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [revealToken, setRevealToken] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HeadlessToken | null>(null);

  const { data: tokens = [], isLoading } = useQuery<HeadlessToken[]>({
    queryKey: ['cms-headless-tokens'],
    queryFn: () => api.get('/api/v1/cms/tokens').then((r) => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (body: { name: string; expiresAt?: string }) =>
      api.post('/api/v1/cms/tokens', body).then((r) => r.data as HeadlessToken & { token: string }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['cms-headless-tokens'] });
      setShowCreate(false);
      setName('');
      setExpiresAt('');
      setRevealToken(result.token);
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/cms/tokens/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-headless-tokens'] });
      toast.success('Token revoked');
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(extractError(err)),
  });

  function handleCreate() {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    createMutation.mutate({
      name: name.trim(),
      ...(expiresAt ? { expiresAt } : {}),
    });
  }

  const isExpired = (t: HeadlessToken) => t.expiresAt != null && new Date(t.expiresAt) < new Date();

  return (
    <div className="space-y-6">
      <PageHeader
        title="API Tokens"
        description="Manage headless CMS delivery tokens for external integrations."
        action={
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus size={14} className="mr-1" />
            New Token
          </Button>
        }
      />

      {isLoading ? (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <tbody>
              <SkeletonRows count={2} cols={4} />
            </tbody>
          </table>
        </div>
      ) : tokens.length === 0 ? (
        <EmptyState
          icon={<Key size={24} />}
          title="No API tokens yet"
          description="Create a token to access your headless CMS entries from external apps."
        />
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100 text-left">
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wide">
                  Name
                </th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wide">
                  Expires
                </th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wide">
                  Last Used
                </th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wide">
                  Created
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-50">
              {tokens.map((token) => (
                <tr key={token.id} className="hover:bg-zinc-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-zinc-900">{token.name}</td>
                  <td
                    className={`px-4 py-3 text-xs ${isExpired(token) ? 'text-red-500' : 'text-zinc-400'}`}
                  >
                    {isExpired(token) ? (
                      <span className="font-medium">Expired</span>
                    ) : (
                      fmt(token.expiresAt)
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{fmt(token.lastUsedAt)}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{fmt(token.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end">
                      <button
                        title="Revoke"
                        onClick={() => setDeleteTarget(token)}
                        className="p-1.5 rounded hover:bg-red-50 text-zinc-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      <Modal
        open={showCreate}
        title="New API Token"
        onClose={() => {
          setShowCreate(false);
          setName('');
          setExpiresAt('');
        }}
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Mobile App Token"
            autoFocus
          />
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">
              Expiry date <span className="text-zinc-400 font-normal">(optional)</span>
            </label>
            <input
              type="date"
              className="w-full border border-zinc-200 rounded-lg text-sm px-3 h-9 focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
        </div>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowCreate(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Token reveal */}
      {revealToken && <TokenRevealModal token={revealToken} onClose={() => setRevealToken(null)} />}

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Revoke Token"
        message={deleteTarget ? `"${deleteTarget.name}" will be permanently revoked.` : ''}
        confirmLabel="Revoke"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
